import { DocumentationComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/documentation-component";
import {
  Documentation,
  ServiceContainer,
  UpdateAdditionalCostPerYearRequest,
  UpdateAdditionalTimePerMeasureRequest,
  UpdateTravelingDurationRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import t from "@/lang/lang";
import {
  TimeNumberInput,
  TimeUnit,
} from "@/shared/components/inputs/time-number-input";
import { CalculationServiceTypeUtil } from "@/services/backend/qpm/calculations/lib/service-type-util";
import { Label } from "@/shared/components/ui/label";
import { useEffect, useMemo, useState } from "react";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import {
  useQpmCalcServiceGroupAdditionalCostPerYearPostMutation,
  useQpmCalcServiceGroupAdditionalTimePerMeasurePostMutation,
  useQpmCalcServiceGroupTravelingDurationPostMutation,
} from "@/services/backend/qpm/calculations/api/calculation-api";
import { CalculationServiceType } from "@/services/backend/qpm/calculations/types/calculation-enum";
import { DocTypeKey } from "@/services/backend/qpm/documentation/types/documentation-enum";
import { TargetRevenueComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/target-revenue-component";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";

export type AdditionalCostComponentProps = {
  calculationId: string;
  serviceGroup: ServiceContainer;
  serviceTypeKey: CalculationServiceType;
  documentations: Record<DocTypeKey, Documentation>;
};

export function AdditionalTimeComponent({
  calculationId,
  serviceGroup,
  serviceTypeKey,
  documentations,
}: AdditionalCostComponentProps) {
  const travelingTimeInputId = `${calculationId}-${serviceTypeKey}-total-traveling-time-input`;
  const additionalTimeInputId = `${calculationId}-${serviceTypeKey}-additional-time-per-measure-input`;
  const additionalCostInputId = `${calculationId}-${serviceTypeKey}-additional-cost-per-year-input`;

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-4">
        <TravelingTimeField
          id={travelingTimeInputId}
          calculationId={calculationId}
          serviceTypeKey={serviceTypeKey}
          travelingDuration={serviceGroup.totalTravelingTime}
        />

        <AdditionalTimePerMeasureField
          id={additionalTimeInputId}
          calculationId={calculationId}
          serviceTypeKey={serviceTypeKey}
          additionalTimePerMeasure={serviceGroup.additionalTimePerMeasure}
        />

        <AdditionalCostPerYearField
          id={additionalCostInputId}
          calculationId={calculationId}
          serviceTypeKey={serviceTypeKey}
          additionalCostPerYear={serviceGroup.additionalCostPerYear}
        />

        <div className="min-w-0 justify-self-start lg:justify-self-end">
          <TargetRevenueComponent
            calculationId={calculationId}
            serviceGroup={serviceGroup}
            serviceTypeKey={serviceTypeKey}
          />
        </div>
      </div>

      <div className="flex flex-col items-start gap-4 border-y p-4">
        <div className="flex items-center justify-between gap-2">
          <Label
            htmlFor={`${calculationId}-${serviceTypeKey}-total-duration-documentation-input`}
          >
            {`${t(
              "Zusatzzeit-Dokumentation",
            )} ${CalculationServiceTypeUtil.toString(serviceTypeKey)}`}
          </Label>
          <TimeNumberInput
            id={`${calculationId}-${serviceTypeKey}-total-duration-documentation-input`}
            value={serviceGroup.totalTimeDocumentation}
            timeUnit={TimeUnit.Minutes}
            disabled
          />
        </div>
        <DocumentationComponent
          calculationId={calculationId}
          selectedDocumentationList={serviceGroup.documentations}
          documentations={documentations}
          serviceTypeKey={serviceTypeKey}
        />
      </div>
    </div>
  );
}

type TravelingTimeFieldProps = {
  id: string;
  calculationId: string;
  serviceTypeKey: CalculationServiceType;
  travelingDuration: number;
};

function TravelingTimeField({
  id,
  calculationId,
  serviceTypeKey,
  travelingDuration,
}: TravelingTimeFieldProps) {
  const [value, setValue] = useState(travelingDuration);

  useEffect(() => {
    setValue(travelingDuration);
  }, [travelingDuration, calculationId, serviceTypeKey]);

  const request = useMemo<UpdateTravelingDurationRequest>(
    () => ({
      calculationId,
      serviceTypeKey,
      travelingDuration: value,
    }),
    [calculationId, serviceTypeKey, value],
  );

  const [mutate, state] = useQpmCalcServiceGroupTravelingDurationPostMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    mutate,
    state.isLoading,
    state.error,
    state.isSuccess,
    state.reset,
    500,
  );

  return (
    <div className="flex items-center justify-between gap-3 lg:justify-start">
      <div className="flex max-w-[18rem] items-center gap-2">
        <Label htmlFor={id}>{t("Gesamtfahrzeit")}</Label>
      </div>
      <div className="shrink-0">
        <TimeNumberInput
          id={id}
          value={value}
          onChange={(val) => setValue(val)}
          timeUnit={TimeUnit.Minutes}
          max={10_000}
          disabled={state.isLoading}
        />
      </div>
    </div>
  );
}

type AdditionalTimePerMeasureFieldProps = {
  id: string;
  calculationId: string;
  serviceTypeKey: CalculationServiceType;
  additionalTimePerMeasure: number | null | undefined;
};

function AdditionalTimePerMeasureField({
  id,
  calculationId,
  serviceTypeKey,
  additionalTimePerMeasure,
}: AdditionalTimePerMeasureFieldProps) {
  const [value, setValue] = useState<number>(additionalTimePerMeasure ?? 0);

  useEffect(() => {
    setValue(additionalTimePerMeasure ?? 0);
  }, [additionalTimePerMeasure, calculationId, serviceTypeKey]);

  const request = useMemo<UpdateAdditionalTimePerMeasureRequest>(
    () => ({
      calculationId,
      serviceTypeKey,
      additionalTimePerMeasure: value,
    }),
    [calculationId, serviceTypeKey, value],
  );

  const [mutate, state] =
    useQpmCalcServiceGroupAdditionalTimePerMeasurePostMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    mutate,
    state.isLoading,
    state.error,
    state.isSuccess,
    state.reset,
    500,
  );

  return (
    <div className="flex items-center justify-between gap-3 lg:justify-start">
      <div className="flex max-w-[18rem] items-center gap-2">
        <Label htmlFor={id}>{t("Sonderzeiten")}</Label>
        <InfoIconTooltip
          id={`${id}-info`}
          text={t(
            "Zum Beispiel: lange Laufwege, Anmeldung, Sicherheitsunterweisung, Abschlußgespräch, hoher Dokumentationsaufwand, wie Eigenkontrollen, Abbau des alten Systems",
          )}
        />
      </div>
      <div className="shrink-0">
        <TimeNumberInput
          id={id}
          value={value}
          onChange={(val) => setValue(val)}
          timeUnit={TimeUnit.Minutes}
          max={10_000}
          disabled={state.isLoading}
        />
      </div>
    </div>
  );
}

type AdditionalCostPerYearFieldProps = {
  id: string;
  calculationId: string;
  serviceTypeKey: CalculationServiceType;
  additionalCostPerYear: number | null | undefined;
};

function AdditionalCostPerYearField({
  id,
  calculationId,
  serviceTypeKey,
  additionalCostPerYear,
}: AdditionalCostPerYearFieldProps) {
  const [value, setValue] = useState<number>(additionalCostPerYear ?? 0);

  useEffect(() => {
    setValue(additionalCostPerYear ?? 0);
  }, [additionalCostPerYear, calculationId, serviceTypeKey]);

  const request = useMemo<UpdateAdditionalCostPerYearRequest>(
    () => ({
      calculationId,
      serviceTypeKey,
      additionalCostPerYear: value,
    }),
    [calculationId, serviceTypeKey, value],
  );

  const [mutate, state] =
    useQpmCalcServiceGroupAdditionalCostPerYearPostMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    mutate,
    state.isLoading,
    state.error,
    state.isSuccess,
    state.reset,
    500,
  );

  return (
    <div className="flex items-center justify-between gap-3 lg:justify-start">
      <div className="flex max-w-[18rem] items-center gap-2">
        <Label htmlFor={id}>{t("Zusatzkosten pro Jahr")}</Label>
        <InfoIconTooltip
          id={`${id}-info`}
          text={t(
            "Zum Beispiel: Übernachtung, Sicherheitsausweis, Sonder-Material",
          )}
        />
      </div>
      <div className="shrink-0">
        <FloatNumberInput
          id={id}
          value={value}
          onChange={(val) => setValue(val)}
          disabled={state.isLoading}
        />
      </div>
    </div>
  );
}
